import { IconButton, makeStyles } from '@material-ui/core';
import CallIcon from '@material-ui/icons/Call';
import SyncAltIcon from '@material-ui/icons/SyncAlt';

import CallMergeIcon from '@material-ui/icons/CallMerge';
import DialpadIcon from '@material-ui/icons/Dialpad';
import MicOffIcon from '@material-ui/icons/MicOff';
import CallEndIcon from '@material-ui/icons/CallEnd';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SyncDisabledIcon from '@material-ui/icons/SyncDisabled';
import React, { useState } from 'react';
import { RecordingIcon } from '../../GlobalAddDealModalV3/components/Icons';
import SendDigitKeypad from "./SendDigitKeypad";
import {useDispatch, useSelector} from "react-redux";
import Utils from "../../../helpers/Utils";
import {CALL_RECORDING_STATUS, GLOBAL_DIALER_CALL_STATUS} from "./index";
import {resetCallEnvironment, setCallEnvironment} from "../../../reducers/globalDialerV2/globalDialerSlice";
import ScrollText from "../../custom/ScrollText";
import BootstrapTooltip from "../../common/BootstrapTooltip";
import {updateRecordStatus} from "../../../api/globalDialerApi";

const useStyles = makeStyles((theme) => ({
    callWrapper: {
        color: '#fff',
        "& .headerSection": {
            display: 'flex !important',
            justifyContent: 'space-between !important',
            alignItems: 'center !important',
            padding: '2px 12px 12px 12px !important',
            borderBottom: '1px solid #e5e8ef !important',

            '&.borderLessHeader': {
                border: 'none !important',
            },

            "& .headerSectionLeft": {
                display: 'flex !important',
                alignItems: 'center !important',
                gap: '8px !important',

                '& .MuiSvgIcon-root': {
                    color: '#fff !important'
                }
            }
        },

        "& .contentSection": {
            padding: "20px 12px 0px 12px !important",

            "& .iconList": {
                display: 'flex !important',
                justifyContent: 'space-between !important',
                alignItems: 'center !important',

                "& .eachIcon": {
                    color: '#fff !important',

                    '&.recording::before': {
                        content: '""',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width: 48,
                        height: 48,
                        backgroundColor: 'rgba(255, 0, 0, 0.5)',
                        borderRadius: '50%',
                        transform: 'translate(-50%, -50%)',
                        animation: '$pulse 1.2s infinite ease-in-out',
                        zIndex: 0,
                    },

                    "&.active": {
                        color: '#f44336 !important',
                    },

                    "& svg": {
                        fontSize: '30px !important',
                        color: '#fff !important'
                    },

                    "&.recordingButton svg": {
                        position: 'relative',
                        zIndex: 1,
                    },
                    
                    "&:hover, &:focus": {
                        backgroundColor: "transparent !important",
                    },

                    '& svg.exitCallIcon': {
                        color: '#ffc905 !important',
                    },

                    '& svg.endCallIcon': {
                        color: '#f52057 !important',
                    }
                }
            },

            '& svg.twoWayConnectIcon': {
                color: '#fff !important'
            }
        }
    },
    defaultButton: {
        backgroundColor: "#fff !important",
        "&:hover, &:focus": {
            backgroundColor: "#fff !important",
        }
    },
    '@keyframes pulse': {
        '0%': {
            transform: 'translate(-50%, -50%) scale(1)',
            opacity: 0.7,
        },
        '70%': {
            transform: 'translate(-50%, -50%) scale(1.8)',
            opacity: 0,
        },
        '100%': {
            transform: 'translate(-50%, -50%) scale(1.8)',
            opacity: 0,
        },
    },
}));

const makeName = (callEnvironment) => {
    let fullName = '';
    let number = '';

    const activeContact = callEnvironment.activeContact;
    const dialerNumber = callEnvironment.dialerNumber;
    const transferToUser = callEnvironment.transferToUser;

    if (activeContact?.first_name){
        fullName = activeContact.first_name;
    }else if (activeContact?.firstName){
        fullName = activeContact.firstName;
    }

    if (activeContact?.last_name){
        if (fullName){
            fullName += " ";
        }

        fullName += activeContact.last_name;
    }else if (activeContact?.lastName){
        if (fullName){
            fullName += " ";
        }

        fullName += activeContact.lastName;
    }

    if (callEnvironment?.callInfo?.callerName){
        fullName = callEnvironment.callInfo.callerName;
    }

    if (transferToUser){
        fullName = transferToUser.full_name;
    }

    if (!fullName){
        fullName = 'Unknown';
    }

    if (activeContact?.number){
        number = activeContact?.number;
    }

    if (dialerNumber){
        number = dialerNumber;
    }

    if (callEnvironment?.callInfo?.callerNumber){
        number = callEnvironment.callInfo.callerNumber;
    }

    return (
        <div>
            {
                fullName &&
                <ScrollText width={100} hoverBehavior="start">
                    <h6 style={{ fontSize: 16, margin: "0 0 4px 0" }}>{fullName}</h6>
                </ScrollText>
            }
            {
                !transferToUser && number &&
                <ScrollText width={100}>
                    <h6 style={{ fontSize: 14, margin: "0 0 4px 0" }}>{Utils.formatNumber(number)}</h6>
                </ScrollText>
            }
        </div>
    );
};

const WarmTransfer = ({ muteCall, sendDigits, mergeCalls, rejectCall, rejectSecondCall, rejectAllCall }) => {
    const dispatch = useDispatch();
    const {callEnvironment, transferCallEnvironment} = useSelector((state)=>(state.globalDialerV2));
    const [callStatus, setCallStatus] = useState({
        isMuted: false,
        isPaused: false
    });
    const [showKeypad, setShowKeypad] = useState(false);

    const classes = useStyles();

    const handleShowKeypad = () => {
        setShowKeypad((prevState)=>(!prevState));
    };

    const handleMuteCall = () => {
        muteCall(!callStatus.isMuted);
        setCallStatus((prevState)=>({
            ...prevState,
            isMuted: !prevState.isMuted
        }));
    };

    const handleMergeCalls = async () => {
        await mergeCalls();
    };

    const handleExitCall = async () => {
      await rejectCall();

      dispatch(resetCallEnvironment());
    };

    const handleCallRecord = async () => {
        if (callEnvironment.callRecordInstance > 0 &&
            callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.STOPPED) {
            window.showNotification('error', 'Recording for this call cannot be restarted once stopped.');
            return;
        }

        const recordStatus = callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.START ? CALL_RECORDING_STATUS.PAUSED : CALL_RECORDING_STATUS.START

        const response = await updateRecordStatus({
            recordStatus: recordStatus,
            callSid: callEnvironment.callInfo.firstCallSid,
            conferenceSid: callEnvironment.callInfo.conferenceSid,
        });

        if (response?.success) {
            dispatch(setCallEnvironment({
                callRecordInstance: callEnvironment.callRecordInstance + 1,
                callRecordingStatus: recordStatus
            }));
        }
    };

    const renderTransferCallStatus = () => {
      if (!transferCallEnvironment.isCallHold && transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.DIALING){
            return <div style={{ fontSize: 14, color: '#00C242' }}>Calling</div>;
      }else if (!transferCallEnvironment.isCallHold && transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RINGING){
          return <div style={{ fontSize: 14, color: '#00C242' }}>Calling</div>;
      }else if (!transferCallEnvironment.isCallHold && transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RECEIVED){
          return <div style={{ fontSize: 14, color: '#00C242' }}>In Call</div>;
      }else if (transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.HANGED_UP){
          return <div style={{ fontSize: 14, color: '#00C242' }}>Hanged Up</div>;
      }else if (transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.LEFT){
          return <div style={{ fontSize: 14, color: '#00C242' }}>Left Call</div>;
      }else if (transferCallEnvironment.isCallHold){
          return <div style={{ fontSize: 14, color: '#FF9900' }}>On Hold</div>;
      }
    };

    const renderCallStatus = () => {
        if (!callEnvironment.isCallHold && callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.DIALING){
            return <div style={{ fontSize: 14, color: '#00C242' }}>Calling</div>;
        }else if (!callEnvironment.isCallHold && callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RINGING){
            return <div style={{ fontSize: 14, color: '#00C242' }}>Calling</div>;
        }else if (!callEnvironment.isCallHold && callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.RECEIVED){
            return <div style={{ fontSize: 14, color: '#00C242' }}>In Call</div>;
        }else if (callEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.HANGED_UP){
            return <div style={{ fontSize: 14, color: '#00C242' }}>Hanged Up</div>;
        }else if (callEnvironment.isCallHold){
            return <div style={{ fontSize: 14, color: '#FF9900' }}>On Hold</div>;
        }
    };

    return (
        <div className={classes.callWrapper}>
            <div  className={showKeypad ? 'headerSection borderLessHeader' : 'headerSection'}>
                <div className='headerSectionLeft'>
                    <CallIcon />
                    <h6 style={{ margin: 0, fontSize: '16px' }}>Transferring Call...</h6>
                </div>
            </div>

            {
                showKeypad &&
                <SendDigitKeypad
                    onKeypress={(digit)=>sendDigits(digit)}
                />
            }

            <div className='contentSection'>
                <div className='' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'center', marginBottom: 16 }}>
                    <div style={{ flex: 1 }}>
                        {makeName(callEnvironment)}
                        {renderCallStatus()}
                    </div>
                    <SyncAltIcon className={'twoWayConnectIcon'} style={{ fontSize: 35, flex: 1 }} />
                    <div style={{ flex: 1 }}>
                        {makeName(transferCallEnvironment)}
                        {renderTransferCallStatus()}
                    </div>
                </div>

                <div className='iconList'>
                    <BootstrapTooltip arrow placement="bottom" title="Mute">
                        <IconButton
                            className={`eachIcon ${callStatus.isMuted && "active"}`}
                            onClick={handleMuteCall}
                        >
                            <MicOffIcon />
                        </IconButton>
                    </BootstrapTooltip>

                    <BootstrapTooltip arrow placement="bottom" title="Dialpad">
                        <IconButton
                            className={`eachIcon ${showKeypad && "active"}`}
                            onClick={handleShowKeypad}
                        >
                            <DialpadIcon />
                        </IconButton>
                    </BootstrapTooltip>

                    <BootstrapTooltip arrow placement="bottom" title={callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.START ? "Stop Recording" : "Record"}>
                        <IconButton
                            className={`eachIcon recordingButton ${callEnvironment.callRecordingStatus === CALL_RECORDING_STATUS.START ? 'recording' : ''}`}
                            onClick={handleCallRecord}
                        >
                            <RecordingIcon height={30} width={30} />
                        </IconButton>
                    </BootstrapTooltip>

                    {callEnvironment.isCallInProgress &&
                        transferCallEnvironment.isCallInProgress && !callEnvironment.isCallHold && (
                            <>
                                {
                                    transferCallEnvironment.callStatus === GLOBAL_DIALER_CALL_STATUS.LEFT ?
                                        <BootstrapTooltip arrow placement="bottom" title="Cancel Transfer">
                                            <IconButton
                                                className={`eachIcon ${callStatus.isPaused && "active"}`}
                                                onClick={rejectSecondCall}
                                            >
                                                <SyncDisabledIcon />
                                            </IconButton>
                                        </BootstrapTooltip> :
                                        <BootstrapTooltip arrow placement="bottom" title="Leave 3-Way Call">
                                            <IconButton
                                                className='eachIcon'
                                                onClick={handleExitCall}
                                            >
                                                <ExitToAppIcon className={'exitCallIcon'} style={{ color: '#ffc905' }}/>
                                            </IconButton>
                                        </BootstrapTooltip>
                                }

                                <BootstrapTooltip arrow placement="bottom" title="End Call For All Parties">
                                    <IconButton
                                        className='eachIcon'
                                        onClick={rejectAllCall}
                                    >
                                        <CallEndIcon className={'endCallIcon'} style={{ color: '#f52057' }}/>
                                    </IconButton>
                                </BootstrapTooltip>
                            </>
                        )}

                    {callEnvironment.isCallInProgress &&
                        transferCallEnvironment.isCallInProgress && callEnvironment.isCallHold && (
                            <>
                                <BootstrapTooltip arrow placement="bottom" title="Cancel Transfer">
                                    <IconButton
                                        className={`eachIcon ${callStatus.isPaused && "active"}`}
                                        onClick={rejectSecondCall}
                                    >
                                        <SyncDisabledIcon />
                                    </IconButton>
                                </BootstrapTooltip>

                                <BootstrapTooltip arrow placement="bottom" title="Merge Call">
                                    <IconButton
                                        className='eachIcon'
                                        onClick={handleMergeCalls}
                                        disabled={transferCallEnvironment.callStatus !== GLOBAL_DIALER_CALL_STATUS.RECEIVED}
                                    >
                                        <CallMergeIcon />
                                    </IconButton>
                                </BootstrapTooltip>
                            </>
                        )}
                </div>
            </div>
        </div>
    );
};

export default WarmTransfer;